import { Document, DocumentStatus, DocumentType, DocumentSubType, PropoundingParty, SubscriptionStatus, Case, Client } from 'briefpoint-client';
import DocumentList from 'components/DocumentList/DocumentList';
import EmptyList from 'components/DocumentList/EmptyList';
import ProcessingBox from 'components/DocumentList/ProcessingBox';
import Help from 'components/Help';
import Loading from 'components/Loading';
import IntroTutorialModal from 'components/Modals/IntroTutorialModal';
import SignUpModal from 'components/Modals/SignUpModal';
import TrialEndsBanner, { GetTrialBannerMessage } from 'components/TrialEndsBanner';
import { useAuth } from 'hooks/useAuth';
import useDocuments from 'hooks/useDocuments';
import { useInterval } from 'hooks/useInterval';
import rg4js from 'raygun4js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, Container } from 'react-bootstrap';
import { useLocation } from 'react-router-dom';
import { DocumentClient, sanitizeFiles } from 'services/DocumentService';
import { FirmClient, hasCanceledSubscription } from 'services/FirmService';
import { classes } from 'utils';
import './DocumentList.scss';
import { HashLink as Link } from "react-router-hash-link";
import { useClientApi, useFirmApi } from 'hooks/useApi';

export function getUploadingDocument(name: string): DocumentClient {
  return {
    id: `${name}-${Math.random()
      .toString(36)
      .replace(/[^a-z]+/g, '')
      .substr(0, 5)}`,
    name,
    info: {
      documentType: DocumentType.Unknown,
      documentSubType: DocumentSubType.Unknown,
      venue: '',
      jurisdiction: '',
      propoundingParty: PropoundingParty.Unknown,
      propoundingPartyName: '',
      respondingPartyName: '',
      caseNumber: '',
      setNumber: 1,
      startingNumber: 1,
    },
    uploadDate: new Date(),
    status: DocumentStatus.Unknown,
    isArchived: false,
  } as DocumentClient;
}

const hasSeenSignUpModalStorageKey = 'hasSeenSignUpModal';

export default function DocumentListScreen() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingDocuments, setUploadingDocuments] = useState<DocumentClient[]>([]);
  const [error, setError] = useState('');
  const [documents, loadDocuments, uploadFiles, archiveDocument, , updateDocument, downloadForm, , downloadRequest] = useDocuments();
  const location = useLocation();
  const didSignUp = new URLSearchParams(location.search).get('signedup');
  const [isSignUpModalShow, setIsSignUpModalShow] = useState(false);
  const [isSignUpModalQuery, setIsSignUpModalQuery] = useState(false);
  const [isIntroTutorialModalShow, setIsIntroTutorialModalShow] = useState(false);
  const [isOver, setIsOver] = useState(false);
  const { user, firm, updatePreferences } = useAuth()!;
  const [allCases, setAllCases] = useState<Case[] | undefined>();
  const [allClients, setAllClients] = useState<Client[] | undefined>();
  const firmApi = useFirmApi();
  const clientApi = useClientApi();
  const infoLoading = useRef(false);

  const nonArchivedDocs = documents?.filter(x => !x.isArchived);

  let processingDocuments = nonArchivedDocs?.filter(
    (d: Document) => (d.status || 0) < DocumentStatus.Processed);
  let erroredDocuments = nonArchivedDocs?.filter((d: Document) => d.status === DocumentStatus.Error);
  const notConfirmedDocuments = nonArchivedDocs?.filter(
    (d: Document) => d.status === DocumentStatus.Processed
  );

  const tempConfirmedDocs = nonArchivedDocs?.filter(
    (d: Document) => d.status === DocumentStatus.InfoConfirmed || d.status === DocumentStatus.Complete
  );

  async function updateDocCaseClient(doc: DocumentClient, _case?: Case, client?: Client) {
    await updateDocument(doc);
    if (_case?.id && !allCases?.find(x => x.id === _case.id)) {
      setAllCases(a => a ? [...a, _case] : [_case]);
    }

    if (client?.id && !allClients?.find(x => x.id === client.id)) {
      setAllClients(a => a ? [...a, client] : [client]);
    }
  }
  const hasDocuments = !!tempConfirmedDocs?.length;
  useInterval(loadDocuments, processingDocuments && processingDocuments.length > 0 ? 3000 : null);

  function handleChooseFile(e: React.MouseEvent) {
    e.preventDefault();
    fileInputRef?.current?.click();
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    await handleDropFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    setIsOver(true);
    e.preventDefault();
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    setIsOver(false);
    e.preventDefault();
  };

  const handleDismissTutorial = useCallback(() => {
    setIsIntroTutorialModalShow(false);

    if (user?.preferences) {
      user.preferences.hasSeenIntroTutorial = true;
      updatePreferences(user.preferences);
    }
  }, [user, updatePreferences]);

  useEffect(() => {
    if (user) {
      if (!isSignUpModalQuery) {
        const showIntroTutorial = !!user?.preferences && !user.preferences.hasSeenIntroTutorial;
        setIsIntroTutorialModalShow(showIntroTutorial);
      } else {
        handleDismissTutorial();
      }
    }
  }, [user, isSignUpModalQuery, handleDismissTutorial]);

  useEffect(() => {
    if (user && firm) {
      shouldShowSignUpWindow(firm);
    }
  }, [user, firm]);

  useEffect(() => {
    async function load() {
      if (!infoLoading.current && user && !allCases && !allClients) {
        infoLoading.current = true;
        const [cases, clients] = await Promise.all([firmApi.firmGetFirmCases({ firmId: user.firmId! }), clientApi.clientGetAll({ firmId: user.firmId! })]);
        setAllCases(cases);
        setAllClients(clients);
      }
    };
    load();
  }, [user, allCases, allClients, clientApi, firmApi]);

  function shouldShowSignUpWindow(firm: FirmClient) {
    const query = new URLSearchParams(window.location.search).get('showSignUp');
    const queryShouldShowSignUp = !!query && !localStorage.getItem(hasSeenSignUpModalStorageKey) && firm.billingInfo?.subscriptionStatus !== SubscriptionStatus.Active;
    setIsSignUpModalShow(queryShouldShowSignUp);
    setIsSignUpModalQuery(queryShouldShowSignUp);
  }

  async function handleUpload(fileList: FileList) {
    if (user!.isInactive || hasCanceledSubscription(firm!)) {
      showSignupPage();
      return;
    }
    setError('');
    var files = Array.from(fileList);
    if (files.length) {
      const sanitizedFiles = sanitizeFiles(files);
      if (sanitizedFiles.length) {
        var newUploadingDocs = sanitizedFiles.map((file) => getUploadingDocument(file.name));
        setUploadingDocuments([...uploadingDocuments, ...newUploadingDocs]);
        await uploadFiles(sanitizedFiles);
        setUploadingDocuments([...uploadingDocuments.filter((doc) => !newUploadingDocs.includes(doc))]);
      } else {
        setError('Only .pdf files are currently supported.');
      }
    }
  }

  async function handleDropFiles(files: FileList) {
    handleUpload(files);
    setIsOver(false);
  }

  async function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { files } = e.target;
    if (files) {
      handleUpload(files);
    }
    e.target.value = '';
  }

  function showSignupPage() {
    rg4js('trackEvent', {
      type: 'pageView',
      path: `/signup-modal`
    });
    setIsSignUpModalShow(true);
  }

  function handleDismissSignUp() {
    localStorage.setItem(hasSeenSignUpModalStorageKey, 'true');
    setIsSignUpModalShow(false);
  }

  const trialEndsMessage = GetTrialBannerMessage(user, firm);
  const showCountdownStyle = firm?.billingInfo?.creditsExpireDate ? true : false;
  const trialEnds = <TrialEndsBanner onClickSignUp={() => showSignupPage()} message={trialEndsMessage} showCountdownStyle={showCountdownStyle} />;

  return (
    <Container className="page-container">
      {/* <Alert variant="danger">Briefpoint is currently experiencing a service outage preventing newly uploaded documents from processing, we apologize for the inconvenience and will remove this banner when the issue is resolved.</Alert> */}
      {trialEnds}
      {error && <Alert variant="danger">{error}</Alert>}
      {didSignUp && <Alert variant="success">Success! Signup complete.</Alert>}
      <header className="d-flex justify-content-between mb-3">
        <h1>Document List</h1>
        <div>
          <Button variant="primary" onClick={handleChooseFile} style={{ marginRight: '15px' }}>
            Upload Document
          </Button>
          <Help id="document-help" text='Briefpoint currently accepts the following California state court documents: <ul><li class="tooltip-row">Requests for Admission</li><li class="tooltip-row">Requests for Production</li><li class="tooltip-row">Special Interrogatories</li><li class="tooltip-row">Form Interrogatories</li></ul>' direction={'bottom'} />
        </div>
        <input
          type="file"
          id="file-input"
          name="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          className="d-none"
          multiple
        />
      </header>
      {uploadingDocuments &&
        uploadingDocuments.length > 0 &&
        uploadingDocuments.map((doc) => (
          <ProcessingBox key={doc.id} document={doc} isComplete={false} archiveDocument={archiveDocument} processingType='uploading' />
        ))}
      {erroredDocuments &&
        erroredDocuments.length > 0 &&
        erroredDocuments.map((doc) => (
          <ProcessingBox key={doc.id} document={doc} isComplete={false} archiveDocument={archiveDocument} />
        ))}
      {processingDocuments &&
        processingDocuments.length > 0 &&
        processingDocuments.map((doc) => (
          <ProcessingBox key={doc.id} document={doc} isComplete={false} archiveDocument={archiveDocument} />
        ))}
      {notConfirmedDocuments &&
        notConfirmedDocuments.length > 0 &&
        notConfirmedDocuments.map((doc) => (
          <ProcessingBox key={doc.id} document={doc} isComplete={true} archiveDocument={archiveDocument} />
        ))}
      {/* <h2 className="mt-5">Recent Documents</h2> */}
      {/* <hr /> */}
      <Loading className='parentDocList' isLoading={documents === null}>
        <div className={classes('documents-wrapper', { dropping: isOver })} onDrop={handleDrop} onDragOver={handleDragOver} onDragEnter={handleDragEnter} onDragLeave={handleDragLeave}>
          {hasDocuments ? (
            <DocumentList documents={tempConfirmedDocs!} archiveDocument={archiveDocument} updateDocument={updateDocCaseClient} allCases={allCases} allClients={allClients} downloadForm={downloadForm} downloadRequest={downloadRequest} />
          ) : (
            <EmptyList chooseFile={handleChooseFile} />
          )}
          <p className='archive'><Link to="/documents/archive">View Archived Documents</Link></p>
        </div>
      </Loading>
      {trialEndsMessage && <SignUpModal isShow={isSignUpModalShow} onClose={handleDismissSignUp} />}
      <IntroTutorialModal isShow={isIntroTutorialModalShow} onClose={handleDismissTutorial} fileUpload={handleChooseFile} />
    </Container>
  );
}